import React, { useState, useEffect } from 'react';
import { Admin, Resource } from 'react-admin';
import Polyglot from 'node-polyglot';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from 'react-query';

import authProvider from './authProvider';
import dataProvider from './dataProvider';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment-timezone';
import 'moment/locale/ru';
import { createBrowserHistory as createHistory } from 'history';

import i18n from './i18n';
import createAdminStore from './createAdminStore';

import { Layout } from './layout';
import { Dashboard } from './home';
import theme from './theme';
import SnackbarNotifications from '~/utils/SnackbarNotifications';
import { setStoreRef } from '~/utils/storeRef';
import onError from './errorsHandler';

import accounts from './resources/accounts';
import individuals from './resources/individuals';
import drafts from './resources/drafts';
import businesses from './resources/businesses';
import solutions from './resources/solutions';
import configurations from './resources/configurations';
import auditLog from './resources/auditLog';
import identities from './resources/identities';
import apiIdentities from './resources/apiIdentities';
import localization from './resources/localization';
import locations from './resources/locations';
import terminals from './resources/terminals';
import employees from './resources/employees';
import externalProfileLinks from './resources/externalProfileLinks';
import paymentDetails from './resources/paymentDetails';
import pricings from '~/resources/pricings';
import partners from '~/resources/partners';
import pnlAccount from '~/resources/pnlAccount';
import bankAccount from './resources/bankAccount';
import currencies from './resources/currencies';
import FXMappingList from './resources/currencies/components/FXMappingList';
import headTransactions from './resources/headTransactions';
import deals from './resources/deals';
import actionReports from './resources/actionReports';
import events from './resources/events';
import batches from './resources/batches';
import paymentServices from './resources/paymentServices';
import roles from './resources/roles';
import profileTags from './resources/profileTags';
import menu from './resources/menu';
import mappings from './resources/menu/mappings';
import transfers from './resources/transfers';
import mambu from './resources/ledgers/mambu';
import applicationRules from './resources/applicationRules';
import scripts from './resources/scripts';
import { queueApprovals, historyApprovals } from './resources/approvals';

import Preloader from '~/components/Preloader';
import { YMInitializer } from 'react-yandex-metrika';
import { getTenantBranding } from './api/utils';
import { merge } from 'lodash';
import { Route } from 'react-router-dom';
import {
  VirtualLiquidityPositionList,
  EndUsersActivityList,
  PartnersReport,
  TenantAccountsReport,
} from './resources/reports';
import { LEDGERS_MAMBU } from '~/utils/constants/resources';
// import ym from 'react-yandex-metrika';

let locale = 'en';
moment.locale(locale);

const polyglot = new Polyglot({ locale, phrases: (i18n as any)[locale] });

let translate = polyglot.t.bind(polyglot);

export const i18nProvider = {
  translate: (key: string, options: any) => translate(key, options),
  changeLocale: (newLocale: string) => {
    locale = newLocale;
    return new Promise(() => {
      // load new messages and update the translate function
    });
  },
  getLocale: () => locale,
};

export const history = createHistory();
export const { store, persistor } = createAdminStore({
  authProvider,
  dataProvider,
  history,
});
setStoreRef(store);

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError,
    },
    queries: {
      onError,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const App = () => {
  const [branding, setBranding] = useState<any | undefined>();
  useEffect(() => {
    getTenantBranding().then((data) => {
      setBranding(data);
    });
  }, []);
  useEffect(() => {
    if (branding) {
      const link = document.querySelector('link[rel="shortcut icon"]');
      if (link) {
        const base64String = branding.theme.favicon;
        link.setAttribute('href', base64String || '/veengu.ico');
      }
    }
  }, [branding]);

  if (!branding) {
    return null;
  }

  branding.theme = merge(theme, branding.theme || {});

  const customRoutes = [
    <Route
      exact
      path='/reports/virtualLiquidityPosition'
      component={VirtualLiquidityPositionList}
      key='accounts-tabs'
    />,
    <Route
      exact
      path='/reports/endUsersActivity'
      component={EndUsersActivityList}
      key='end-users-activity'
    />,
    <Route exact path='/reports/partners' component={PartnersReport} key='partners-report' />,
    <Route
      exact
      path='/reports/tenantAccounts'
      component={TenantAccountsReport}
      key='tenant-accounts-report'
    />,
  ];

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Preloader>
                <SnackbarNotifications />
                <Admin
                  authProvider={authProvider}
                  dataProvider={dataProvider}
                  i18nProvider={i18nProvider}
                  layout={Layout}
                  loginPage={Preloader}
                  dashboard={Dashboard}
                  theme={branding.theme}
                  history={history}
                  disableTelemetry
                  customRoutes={customRoutes}
                >
                  <Resource name='accounts' {...accounts} />
                  <Resource name='externalSources' />
                  <Resource name='individuals/drafts' {...drafts} />
                  <Resource name='individuals' {...individuals} />
                  <Resource name='individuals/accounts' />
                  <Resource name='tenant/accounts' {...pnlAccount} />
                  <Resource name='bank/accounts' {...bankAccount} />
                  <Resource name='businesses/drafts' {...drafts} />
                  <Resource name='businesses' {...businesses} />
                  <Resource name='businesses/accounts' />
                  <Resource name='locations' {...locations} />
                  <Resource name='terminals' {...terminals} />
                  <Resource name='employees' {...employees} />
                  <Resource name='paymentDetails' {...paymentDetails} />
                  <Resource name='products' />
                  <Resource name='paymentInstrumentAvailabilityRules' />
                  <Resource name='pricings/drafts' {...drafts} />
                  <Resource name='pricings' {...pricings} />
                  <Resource name='productTemplates' />
                  <Resource name='features' />
                  <Resource name='configurations/settings' {...configurations} />
                  <Resource name='configurations/pricing' {...configurations} />
                  <Resource name='solutions' {...solutions} />
                  <Resource name='partners' {...partners} />
                  <Resource name='auditLog' {...auditLog} />
                  <Resource name='roles' {...roles} />
                  <Resource name='fxrates' {...currencies} />
                  <Resource name='fxrates/mappings' list={FXMappingList} />
                  <Resource name='headTransactions' {...headTransactions} />
                  <Resource name='deals' {...deals} />
                  <Resource name='beneficiaries' />
                  <Resource name='team' {...identities} />
                  <Resource name='apiIdentities' {...apiIdentities} />
                  <Resource name='actionReports' {...actionReports} />
                  <Resource name='events' {...events} />
                  <Resource name='batches' {...batches} />
                  <Resource name='paymentServices' {...paymentServices} />
                  <Resource name='localization' {...localization} />
                  <Resource name='externalProfileLinks' {...externalProfileLinks} />
                  <Resource name='profileTags' {...profileTags} />
                  <Resource name='legalConsents' />
                  <Resource name='menu/mappings' {...mappings} />
                  <Resource name='menu' {...menu} />
                  <Resource name='applicationRules' {...applicationRules} />
                  <Resource name='scripts' {...scripts} />
                  <Resource name='transaction/transfers/search' {...transfers} />
                  <Resource name='transaction/transfers' {...transfers} />
                  <Resource name={LEDGERS_MAMBU} {...mambu} />
                  <Resource name='approvals/queue' {...queueApprovals} />
                  <Resource name='approvals/history' {...historyApprovals} />
                  <Resource name='applications' />
                  <Resource name='externalAccountLinks' />
                </Admin>
              </Preloader>
            </MuiPickersUtilsProvider>
          </PersistGate>
        </Provider>
      </QueryClientProvider>
      {window.location.hostname === 'demo.veengu.net' ? (
        <YMInitializer
          accounts={[65837992]}
          options={{
            webvisor: true,
            trackHash: true,
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
          }}
          version='2'
        />
      ) : null}
    </>
  );
};

export default App;
